import { useState, useEffect } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'
import { MainLayout } from '@/components/layout'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/hooks/use-toast'
import {
  Building2,
  CreditCard,
  ArrowLeft,
  Save,
  Edit3,
  Mail,
  Phone,
  MapPin,
  Clock,
  Users,
  FileText,
  CalendarDays,
  Loader2,
  MessageCircle,
  Inbox,
  CheckCircle2,
} from 'lucide-react'
import {
  useOverlapPolicy,
  useUpdateOverlapPolicy,
} from '@/features/scheduling/hooks/use-overlap-policy'
import {
  OVERLAP_POLICY_LABELS,
  type SchedulingOverlapPolicy,
} from '@/features/scheduling/types/scheduling.types'

export const Route = createFileRoute('/_authenticated/admin/settings')({
  component: AdminSettingsPage,
})

interface OrganizationSettings {
  id: string
  name: string
  document_number: string | null
  phone: string | null
  email: string | null
  address: string | null
  city: string | null
  state: string | null
  zip_code: string | null
  subscription_status: string
  billing_current_period_end: string | null
  billing_plan_id: string | null
}

interface OrganizationBillingSnapshot {
  organization_id: string
  subscription_id: string | null
  plan_id: string | null
  billing_status: string
  current_period_end: string | null
  source: string
}

interface UsageStats {
  professionals: number
  patients: number
  reportsThisMonth: number
}

function AdminSettingsPage() {
  useDocumentTitle('Configurações Administrativas')
  const navigate = useNavigate()
  const { currentOrganization, isAdmin } = useAuth()
  const { toast } = useToast()

  const [orgSettings, setOrgSettings] = useState<OrganizationSettings | null>(
    null,
  )
  const [usageStats, setUsageStats] = useState<UsageStats>({
    professionals: 0,
    patients: 0,
    reportsThisMonth: 0,
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<
    'organization' | 'subscription' | 'scheduling'
  >('organization')
  const [editingOrg, setEditingOrg] = useState(false)

  useEffect(() => {
    if (!currentOrganization) return
    const organizationId = currentOrganization.id

    async function loadSettings() {
      try {
        const [orgResult, billingResult, professionalsResult, patientsResult] =
          await Promise.all([
            supabase
              .from('organizations')
              .select('*')
              .eq('id', organizationId)
              .single(),
            supabase
              .from('v_organization_billing_snapshot')
              .select(
                'organization_id, subscription_id, plan_id, billing_status, current_period_end, source',
              )
              .eq('organization_id', organizationId)
              .maybeSingle(),
            supabase
              .from('organization_members')
              .select('profile_id')
              .eq('organization_id', organizationId),
            supabase
              .from('patients')
              .select('id, created_at')
              .eq('organization_id', organizationId),
          ])

        if (orgResult.error) throw orgResult.error

        if (billingResult.error) {
          console.warn(
            'Billing snapshot unavailable, falling back to legacy organization fields:',
            billingResult.error,
          )
        }

        const billingSnapshot = billingResult.error
          ? null
          : (billingResult.data as OrganizationBillingSnapshot | null)

        setOrgSettings({
          ...orgResult.data,
          subscription_status:
            billingSnapshot?.billing_status ??
            orgResult.data.subscription_status ??
            'none',
          billing_current_period_end:
            billingSnapshot?.current_period_end ??
            orgResult.data.trial_ends_at ??
            null,
          billing_plan_id: billingSnapshot?.plan_id ?? null,
        })

        const startOfMonth = new Date()
        startOfMonth.setDate(1)
        startOfMonth.setHours(0, 0, 0, 0)

        const patientsThisMonth =
          patientsResult.data?.filter(
            (patient) => new Date(patient.created_at) >= startOfMonth,
          ) || []

        setUsageStats({
          professionals: professionalsResult.data?.length || 0,
          patients: patientsResult.data?.length || 0,
          reportsThisMonth: patientsThisMonth.length,
        })
      } catch (error) {
        console.error('Error loading settings:', error)
        toast({
          title: 'Erro',
          description: 'Erro ao carregar configuracoes',
          variant: 'destructive',
        })
      } finally {
        setLoading(false)
      }
    }

    void loadSettings()
  }, [currentOrganization, toast])

  const saveOrganizationSettings = async () => {
    if (!orgSettings || !currentOrganization) return

    setSaving(true)
    try {
      const { error } = await supabase
        .from('organizations')
        .update({
          name: orgSettings.name,
          document_number: orgSettings.document_number,
          phone: orgSettings.phone,
          email: orgSettings.email,
          address: orgSettings.address,
          city: orgSettings.city,
          state: orgSettings.state,
          zip_code: orgSettings.zip_code,
        })
        .eq('id', currentOrganization.id)

      if (error) throw error

      toast({
        title: 'Sucesso',
        description: 'Configurações salvas com sucesso!',
      })
      setEditingOrg(false)
    } catch (error) {
      console.error('Error saving settings:', error)
      toast({
        title: 'Erro',
        description: 'Erro ao salvar configuracoes',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Nao definido'
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const getSubscriptionStatus = (
    status: string,
  ): { label: string; color: string } => {
    const statuses: Record<string, { label: string; color: string }> = {
      active: { label: 'Ativo', color: 'bg-green-100 text-green-700' },
      trial: { label: 'Periodo de Teste', color: 'bg-blue-100 text-blue-700' },
      past_due: {
        label: 'Pagamento em Atraso',
        color: 'bg-amber-100 text-amber-700',
      },
      suspended: { label: 'Suspenso', color: 'bg-orange-100 text-orange-700' },
      cancelled: { label: 'Cancelado', color: 'bg-gray-100 text-gray-700' },
      churned: { label: 'Cancelado', color: 'bg-gray-100 text-gray-700' },
      none: { label: 'Sem Assinatura', color: 'bg-slate-100 text-slate-700' },
    }
    return (
      statuses[status] ?? {
        label: status || 'Não definido',
        color: 'bg-slate-100 text-slate-700',
      }
    )
  }

  if (!isAdmin) {
    return (
      <MainLayout
        title="Acesso negado"
        subtitle="Você não tem permissão para acessar esta página"
      >
        <div className="flex flex-col items-center justify-center py-12">
          <p className="text-gray-600 mb-4">
            Apenas administradores podem acessar as configurações.
          </p>
          <Button onClick={() => navigate({ to: '/dashboard' })}>
            Voltar ao Dashboard
          </Button>
        </div>
      </MainLayout>
    )
  }

  if (loading) {
    return (
      <MainLayout
        title="Configurações administrativas"
        subtitle="Carregando configurações..."
      >
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <Card className="p-4">
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            </Card>
            <div className="lg:col-span-3">
              <Card className="p-6">
                <div className="space-y-4">
                  <Skeleton className="h-6 w-1/3" />
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout
      title="Configurações administrativas"
      subtitle="Gerencie configurações da organização"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate({ to: '/admin' })}
            className="text-gray-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar à administração
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Menu */}
          <Card className="p-4 h-fit">
            <nav className="space-y-1">
              <button
                onClick={() => setActiveTab('organization')}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'organization'
                    ? 'bg-teal-50 text-teal-700 border border-teal-200'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-teal-700'
                }`}
              >
                <Building2 className="w-4 h-4 mr-3" />
                Organização
              </button>

              <button
                onClick={() => setActiveTab('subscription')}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'subscription'
                    ? 'bg-teal-50 text-teal-700 border border-teal-200'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-teal-700'
                }`}
              >
                <CreditCard className="w-4 h-4 mr-3" />
                Assinatura
              </button>

              <button
                onClick={() => setActiveTab('scheduling')}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'scheduling'
                    ? 'bg-teal-50 text-teal-700 border border-teal-200'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-teal-700'
                }`}
              >
                <CalendarDays className="w-4 h-4 mr-3" />
                Agendamento
              </button>
            </nav>
          </Card>

          {/* Content */}
          <div className="lg:col-span-3">
            {activeTab === 'organization' && orgSettings && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Informações da organização</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingOrg(!editingOrg)}
                  >
                    <Edit3 className="w-4 h-4 mr-1" />
                    {editingOrg ? 'Cancelar' : 'Editar'}
                  </Button>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Nome da organização</Label>
                      <Input
                        value={orgSettings.name}
                        onChange={(e) =>
                          setOrgSettings({
                            ...orgSettings,
                            name: e.target.value,
                          })
                        }
                        disabled={!editingOrg}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>CNPJ/CPF</Label>
                      <Input
                        value={orgSettings.document_number || ''}
                        onChange={(e) =>
                          setOrgSettings({
                            ...orgSettings,
                            document_number: e.target.value,
                          })
                        }
                        disabled={!editingOrg}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        Telefone
                      </Label>
                      <Input
                        type="tel"
                        value={orgSettings.phone || ''}
                        onChange={(e) =>
                          setOrgSettings({
                            ...orgSettings,
                            phone: e.target.value,
                          })
                        }
                        disabled={!editingOrg}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        Email
                      </Label>
                      <Input
                        type="email"
                        value={orgSettings.email || ''}
                        onChange={(e) =>
                          setOrgSettings({
                            ...orgSettings,
                            email: e.target.value,
                          })
                        }
                        disabled={!editingOrg}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      Endereco
                    </Label>
                    <Input
                      value={orgSettings.address || ''}
                      onChange={(e) =>
                        setOrgSettings({
                          ...orgSettings,
                          address: e.target.value,
                        })
                      }
                      disabled={!editingOrg}
                      placeholder="Rua, numero, complemento"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label>Cidade</Label>
                      <Input
                        value={orgSettings.city || ''}
                        onChange={(e) =>
                          setOrgSettings({
                            ...orgSettings,
                            city: e.target.value,
                          })
                        }
                        disabled={!editingOrg}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Estado</Label>
                      <Input
                        value={orgSettings.state || ''}
                        onChange={(e) =>
                          setOrgSettings({
                            ...orgSettings,
                            state: e.target.value,
                          })
                        }
                        disabled={!editingOrg}
                        placeholder="SP"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>CEP</Label>
                      <Input
                        value={orgSettings.zip_code || ''}
                        onChange={(e) =>
                          setOrgSettings({
                            ...orgSettings,
                            zip_code: e.target.value,
                          })
                        }
                        disabled={!editingOrg}
                        placeholder="00000-000"
                      />
                    </div>
                  </div>

                  {editingOrg && (
                    <div className="pt-4 border-t">
                      <Button
                        onClick={saveOrganizationSettings}
                        disabled={saving}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? 'Salvando...' : 'Salvar Alteracoes'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === 'scheduling' && (
              <SchedulingSettingsTab organizationId={currentOrganization?.id} />
            )}

            {activeTab === 'subscription' && orgSettings && (
              <Card>
                <CardHeader>
                  <CardTitle>Informações da assinatura</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Current Plan */}
                  <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg p-6 border border-teal-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          Plano Atual
                        </h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              getSubscriptionStatus(
                                orgSettings.subscription_status,
                              ).color
                            }`}
                          >
                            {
                              getSubscriptionStatus(
                                orgSettings.subscription_status,
                              ).label
                            }
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Expira em</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {formatDate(orgSettings.billing_current_period_end)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Features */}
                    <div className="bg-white border rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">
                        Recursos Inclusos
                      </h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Usuarios ilimitados</li>
                        <li>• Pacientes ilimitados</li>
                        <li>• Relatos automáticos</li>
                        <li>• Backup diario</li>
                        <li>• Suporte por email</li>
                      </ul>
                    </div>

                    {/* Usage */}
                    <div className="bg-white border rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">
                        Uso Atual
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            Profissionais:
                          </span>
                          <span className="font-medium">
                            {usageStats.professionals}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            Pacientes:
                          </span>
                          <span className="font-medium">
                            {usageStats.patients}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 flex items-center gap-1">
                            <FileText className="w-4 h-4" />
                            Cadastros este mes:
                          </span>
                          <span className="font-medium">
                            {usageStats.reportsThisMonth}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Renewal Notice */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Clock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-blue-800">
                          Renovacao Automatica
                        </p>
                        <p className="text-sm text-blue-700 mt-1">
                          Sua assinatura sera renovada automaticamente em{' '}
                          {formatDate(orgSettings.billing_current_period_end)}.
                          Para alterar o plano ou cancelar, entre em contato com
                          o suporte.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <Button variant="outline" asChild>
                      <a href="mailto:info@secbra.in?subject=Suporte SecBrain Neurodivergente">
                        <Mail className="w-4 h-4 mr-2" />
                        Contatar Suporte
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  )
}

// ============================================================================
// Scheduling Settings Tab
// ============================================================================

const POLICY_OPTIONS: {
  value: SchedulingOverlapPolicy
  description: string
}[] = [
  {
    value: 'allowed',
    description: 'Profissionais podem ter atendimentos sobrepostos sem aviso.',
  },
  {
    value: 'allowed_with_warning',
    description:
      'Um aviso sera exibido ao tentar agendar em horario conflitante, mas o profissional pode confirmar.',
  },
  {
    value: 'not_allowed',
    description:
      'Nao sera possivel agendar se houver conflito de horario para o profissional.',
  },
]

type WhatsAppAdminRequestMode = 'disabled' | 'request_only'

type WhatsAppAdminRequestType =
  | 'appointment_confirmation'
  | 'reschedule_request'
  | 'cancellation_request'
  | 'admin_question'
  | 'unknown'

interface WhatsAppAdminSettingsState {
  adminRequestMode: WhatsAppAdminRequestMode
  adminContactText: string
  confirmAttendanceEnabled: boolean
}

interface WhatsAppAdminRequestRow {
  id: string
  request_type: WhatsAppAdminRequestType
  status: 'open' | 'reviewed' | 'resolved' | 'dismissed'
  created_at: string
  message_text: string | null
  sender_phone: string
  patient: { full_name: string } | null
  session: { session_date: string; start_time: string | null } | null
}

const WHATSAPP_ADMIN_MODE_OPTIONS: Array<{
  value: WhatsAppAdminRequestMode
  label: string
  description: string
}> = [
  {
    value: 'disabled',
    label: 'Desabilitado',
    description:
      'O WhatsApp responde com o canal da clinica e nao cria solicitacao aberta.',
  },
  {
    value: 'request_only',
    label: 'Criar solicitacao',
    description:
      'Pedidos administrativos viram uma pendencia para a equipe, sem resposta livre do agente.',
  },
]

const ADMIN_REQUEST_TYPE_LABELS: Record<WhatsAppAdminRequestType, string> = {
  appointment_confirmation: 'Confirmacao de presenca',
  reschedule_request: 'Pedido de remarcacao',
  cancellation_request: 'Pedido de cancelamento',
  admin_question: 'Duvida administrativa',
  unknown: 'Mensagem administrativa',
}

function SchedulingSettingsTab({
  organizationId,
}: {
  organizationId?: string
}) {
  const { toast } = useToast()
  const { data: currentPolicy, isLoading } = useOverlapPolicy()
  const updatePolicy = useUpdateOverlapPolicy()
  const [selectedPolicy, setSelectedPolicy] =
    useState<SchedulingOverlapPolicy | null>(null)
  const [whatsappSettings, setWhatsappSettings] =
    useState<WhatsAppAdminSettingsState | null>(null)
  const [initialWhatsappSettings, setInitialWhatsappSettings] =
    useState<WhatsAppAdminSettingsState | null>(null)
  const [adminRequests, setAdminRequests] = useState<WhatsAppAdminRequestRow[]>(
    [],
  )
  const [loadingWhatsapp, setLoadingWhatsapp] = useState(true)
  const [savingWhatsapp, setSavingWhatsapp] = useState(false)
  const [updatingRequestId, setUpdatingRequestId] = useState<string | null>(
    null,
  )

  useEffect(() => {
    if (currentPolicy && selectedPolicy === null) {
      setSelectedPolicy(currentPolicy)
    }
  }, [currentPolicy, selectedPolicy])

  useEffect(() => {
    if (!organizationId) return
    let cancelled = false

    async function loadWhatsAppSettings() {
      setLoadingWhatsapp(true)
      try {
        const [settingsResult, requestsResult] = await Promise.all([
          supabase
            .from('organizations')
            .select(
              'whatsapp_admin_request_mode, whatsapp_admin_contact_text, whatsapp_confirm_attendance_enabled',
            )
            .eq('id', organizationId)
            .single(),
          supabase
            .from('whatsapp_admin_requests')
            .select(
              `
              id,
              request_type,
              status,
              created_at,
              message_text,
              sender_phone,
              patient:patients!whatsapp_admin_requests_patient_id_fkey(full_name),
              session:sessions!whatsapp_admin_requests_session_id_fkey(session_date, start_time)
            `,
            )
            .eq('organization_id', organizationId)
            .eq('status', 'open')
            .order('created_at', { ascending: false })
            .limit(8),
        ])

        if (settingsResult.error) throw settingsResult.error
        if (requestsResult.error) throw requestsResult.error
        if (cancelled) return

        const settings = {
          adminRequestMode:
            settingsResult.data.whatsapp_admin_request_mode === 'request_only'
              ? 'request_only'
              : 'disabled',
          adminContactText:
            settingsResult.data.whatsapp_admin_contact_text ?? '',
          confirmAttendanceEnabled:
            settingsResult.data.whatsapp_confirm_attendance_enabled === true,
        } satisfies WhatsAppAdminSettingsState

        setWhatsappSettings(settings)
        setInitialWhatsappSettings(settings)
        setAdminRequests(
          (requestsResult.data ?? []) as unknown as WhatsAppAdminRequestRow[],
        )
      } catch (error) {
        console.error('Error loading WhatsApp admin settings:', error)
        toast({
          title: 'Erro',
          description: 'Erro ao carregar configuracoes de WhatsApp.',
          variant: 'destructive',
        })
      } finally {
        if (!cancelled) setLoadingWhatsapp(false)
      }
    }

    void loadWhatsAppSettings()

    return () => {
      cancelled = true
    }
  }, [organizationId, toast])

  const hasPolicyChanges =
    selectedPolicy !== null && selectedPolicy !== currentPolicy
  const hasWhatsappChanges =
    whatsappSettings !== null &&
    initialWhatsappSettings !== null &&
    (whatsappSettings.adminRequestMode !==
      initialWhatsappSettings.adminRequestMode ||
      whatsappSettings.adminContactText !==
        initialWhatsappSettings.adminContactText ||
      whatsappSettings.confirmAttendanceEnabled !==
        initialWhatsappSettings.confirmAttendanceEnabled)

  const handleSave = async () => {
    if (!organizationId || !selectedPolicy || !whatsappSettings) return

    setSavingWhatsapp(true)
    try {
      if (hasPolicyChanges) {
        const result = await updatePolicy.mutateAsync({
          organization_id: organizationId,
          policy: selectedPolicy,
        })

        if (!result.success) {
          throw new Error(
            result.error ?? 'Erro ao atualizar politica de agendamento.',
          )
        }
      }

      if (hasWhatsappChanges) {
        const { error } = await supabase
          .from('organizations')
          .update({
            whatsapp_admin_request_mode: whatsappSettings.adminRequestMode,
            whatsapp_admin_contact_text:
              whatsappSettings.adminContactText.trim() || null,
            whatsapp_confirm_attendance_enabled:
              whatsappSettings.confirmAttendanceEnabled,
          })
          .eq('id', organizationId)

        if (error) throw error
        setInitialWhatsappSettings({ ...whatsappSettings })
      }

      toast({
        title: 'Sucesso',
        description: 'Configurações de agendamento atualizadas com sucesso!',
      })
    } catch (error) {
      console.error('Error saving scheduling settings:', error)
      toast({
        title: 'Erro',
        description:
          error instanceof Error
            ? error.message
            : 'Erro ao salvar configuracoes de agendamento.',
        variant: 'destructive',
      })
    } finally {
      setSavingWhatsapp(false)
    }
  }

  const updateAdminRequestStatus = async (
    requestId: string,
    status: 'reviewed' | 'resolved',
  ) => {
    setUpdatingRequestId(requestId)
    try {
      const { error } = await supabase
        .from('whatsapp_admin_requests')
        .update({ status })
        .eq('id', requestId)

      if (error) throw error

      setAdminRequests((requests) =>
        requests.filter((request) => request.id !== requestId),
      )
      toast({
        title: 'Solicitacao atualizada',
        description:
          status === 'resolved'
            ? 'A solicitacao foi marcada como resolvida.'
            : 'A solicitacao foi marcada como revisada.',
      })
    } catch (error) {
      console.error('Error updating WhatsApp admin request:', error)
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar solicitacao.',
        variant: 'destructive',
      })
    } finally {
      setUpdatingRequestId(null)
    }
  }

  if (isLoading || loadingWhatsapp || !whatsappSettings) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5" />
          Configurações de agendamento
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-1">
            Politica de Sobreposicao de Horarios
          </h4>
          <p className="text-sm text-gray-500 mb-4">
            Defina como o sistema deve lidar quando um profissional ja possui um
            atendimento no mesmo horario.
          </p>

          <div className="space-y-3">
            {POLICY_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                  selectedPolicy === option.value
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="overlap_policy"
                  value={option.value}
                  checked={selectedPolicy === option.value}
                  onChange={() => setSelectedPolicy(option.value)}
                  className="mt-1 accent-teal-600"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {OVERLAP_POLICY_LABELS[option.value]}
                  </p>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {option.description}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="border-t pt-6">
          <h4 className="text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            WhatsApp e Agenda
          </h4>
          <p className="text-sm text-gray-500 mb-4">
            Controle o que o WhatsApp pode fazer fora do fluxo de relatos.
          </p>

          <div className="space-y-4">
            <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-4 cursor-pointer">
              <input
                type="checkbox"
                checked={whatsappSettings.confirmAttendanceEnabled}
                onChange={(event) =>
                  setWhatsappSettings({
                    ...whatsappSettings,
                    confirmAttendanceEnabled: event.target.checked,
                  })
                }
                className="mt-1 accent-teal-600"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Permitir confirmacao de presenca
                </p>
                <p className="text-sm text-gray-500 mt-0.5">
                  Respostas claras como "sim" ou "confirmo" podem marcar o
                  atendimento correspondente como confirmado.
                </p>
              </div>
            </label>

            <div>
              <Label className="text-sm font-medium text-gray-900">
                Pedidos administrativos pelo WhatsApp
              </Label>
              <div className="space-y-3 mt-3">
                {WHATSAPP_ADMIN_MODE_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                      whatsappSettings.adminRequestMode === option.value
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="whatsapp_admin_request_mode"
                      value={option.value}
                      checked={
                        whatsappSettings.adminRequestMode === option.value
                      }
                      onChange={() =>
                        setWhatsappSettings({
                          ...whatsappSettings,
                          adminRequestMode: option.value,
                        })
                      }
                      className="mt-1 accent-teal-600"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {option.label}
                      </p>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {option.description}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Canal de contato para orientar familias</Label>
              <Textarea
                value={whatsappSettings.adminContactText}
                onChange={(event) =>
                  setWhatsappSettings({
                    ...whatsappSettings,
                    adminContactText: event.target.value,
                  })
                }
                rows={3}
                placeholder="Ex.: fale com a recepcao pelo telefone (11) 99999-9999 ou pelo email atendimento@clinica.com"
              />
              <p className="text-xs text-gray-500">
                Usado quando o modo esta desabilitado ou quando uma duvida nao
                deve ser respondida pelo agente.
              </p>
            </div>
          </div>
        </div>

        <div className="border-t pt-6">
          <h4 className="text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
            <Inbox className="h-4 w-4" />
            Solicitações abertas do WhatsApp
          </h4>
          <p className="text-sm text-gray-500 mb-4">
            Pedidos classificados como administrativos aparecem aqui para a
            equipe revisar.
          </p>

          {adminRequests.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 p-4 text-sm text-gray-500">
              Nenhuma solicitacao aberta no momento.
            </div>
          ) : (
            <div className="space-y-3">
              {adminRequests.map((request) => (
                <div
                  key={request.id}
                  className="rounded-lg border border-gray-200 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {ADMIN_REQUEST_TYPE_LABELS[request.request_type]}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {request.patient?.full_name ?? 'Paciente nao vinculado'}
                        {request.session
                          ? ` • ${new Date(
                              `${request.session.session_date}T12:00:00`,
                            ).toLocaleDateString(
                              'pt-BR',
                            )} ${request.session.start_time ?? ''}`
                          : ''}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(request.created_at).toLocaleString('pt-BR')}
                    </span>
                  </div>

                  {request.message_text && (
                    <p className="text-sm text-gray-600 mt-3 line-clamp-2">
                      {request.message_text}
                    </p>
                  )}

                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        updateAdminRequestStatus(request.id, 'reviewed')
                      }
                      disabled={updatingRequestId === request.id}
                    >
                      {updatingRequestId === request.id ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                      )}
                      Revisado
                    </Button>
                    <Button
                      size="sm"
                      onClick={() =>
                        updateAdminRequestStatus(request.id, 'resolved')
                      }
                      disabled={updatingRequestId === request.id}
                    >
                      Resolver
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pt-4 border-t">
          <Button
            onClick={handleSave}
            disabled={
              updatePolicy.isPending ||
              savingWhatsapp ||
              (!hasPolicyChanges && !hasWhatsappChanges)
            }
          >
            {updatePolicy.isPending || savingWhatsapp ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Salvar configurações
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
